import { useState, useCallback, useEffect } from 'react';
import FreteService, { Endereco, ResultadoFrete } from '../services/freteService';

interface UseFreteReturn {
  calcularFrete: (endereco: Endereco, empresaId?: number) => Promise<void>;
  resultadoFrete: ResultadoFrete | null;
  loading: boolean;
  error: string | null;
  limparFrete: () => void;
  infoFrete: {
    minimo: number;
    valorPorKm: number;
    areaMaxima: number;
  };
}

interface UseFreteOptions {
  empresaId?: number;
}

// Instância única do FreteService para evitar múltiplas criações
const freteService = new FreteService();
const infoFreteCache = new Map<number, { minimo: number; valorPorKm: number; areaMaxima: number }>();

const useFrete = (options?: UseFreteOptions): UseFreteReturn => {
  const empresaId = options?.empresaId || 1;
  
  const [resultadoFrete, setResultadoFrete] = useState<ResultadoFrete | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [infoFrete, setInfoFrete] = useState<{ minimo: number; valorPorKm: number; areaMaxima: number }>(() => {
    return infoFreteCache.get(empresaId) || {
      minimo: 5.0,
      valorPorKm: 1.0,
      areaMaxima: 25
    };
  });
  
  // Carregar informações do frete ao inicializar ou quando empresaId mudar
  useEffect(() => {
    if (infoFreteCache.has(empresaId)) {
      setInfoFrete(infoFreteCache.get(empresaId)!);
      return;
    }

    const carregarInfoFrete = async () => {
      try {
        const info = await freteService.getInfoFrete(empresaId);
        infoFreteCache.set(empresaId, info);
        setInfoFrete(info);
      } catch {
        // Falha tratada silenciosamente
      }
    };
    
    carregarInfoFrete();
  }, [empresaId]);

  const calcularFrete = useCallback(async (endereco: Endereco, empresaId: number = 1) => {
    setLoading(true);
    setError(null);
    
    try {
      const resultado = await freteService.calcularFrete(endereco, empresaId);
      setResultadoFrete(resultado);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao calcular frete';
      setError(errorMessage);
      setResultadoFrete(null);
      
      // Se for erro de área, não limpar o resultado anterior
      if (errorMessage.includes('não estamos disponíveis')) {
        // Manter o resultado anterior para mostrar a distância
        return;
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const limparFrete = useCallback(() => {
    setResultadoFrete(null);
    setError(null);
  }, []);

  return {
    calcularFrete,
    resultadoFrete,
    loading,
    error,
    limparFrete,
    infoFrete
  };
};

export default useFrete;
export type { Endereco, ResultadoFrete };
